import { useCallback } from 'react';

import type { GridApiPremium, GridRowId } from '@mui/x-data-grid-premium';
import { gridRowNodeSelector } from '@mui/x-data-grid-premium';

import { getIsGroupNode } from './data-grid-premium';

/**
 * Returns a stable callback that toggles a row's expanded/collapsed state in a
 * DataGridPremium tree or grouped view backed by native nested lazy loading.
 *
 * Flipping expansion is all we do: MUI's nested-lazy-loading strategy fetches
 * the row's children off the resulting `rowExpansionChange` event. We must NOT
 * call `dataSource.fetchRows` ourselves to expand — under the lazy strategy
 * that loads the children but leaves the group collapsed (fetch does not drive
 * expansion; expansion drives fetch).
 *
 * @example
 * ```tsx
 * const apiRef = useGridApiRef();
 * const toggleRowExpansion = useToggleRowExpansion(apiRef);
 *
 * // Pass to useGroupingColumns for group header clicks
 * const groupingColDef = useGroupingColumns({ toggleRowExpansion, ... });
 * ```
 */
export function useToggleRowExpansion(apiRef: React.RefObject<GridApiPremium | null>) {
  return useCallback(
    (rowId: GridRowId) => {
      if (!apiRef.current) return;
      const rowNode = gridRowNodeSelector(apiRef, rowId);
      const isExpanded = getIsGroupNode(rowNode) && Boolean(rowNode.childrenExpanded);
      apiRef.current.setRowChildrenExpansion(rowId, !isExpanded);
    },
    [apiRef]
  );
}
